import React, { useEffect, useMemo, useRef, useState } from "react";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { useAppContext } from "../../../contexts/AppContext";
import { getDepRetLegsFromLinkedFlights } from "../../../utils/vacationPackagesLinkedFlights";
import ChatRoomPicker from "./ChatRoomPicker";
import HeroGalleryLightbox from "../../HeroGalleryLightbox/HeroGalleryLightbox";
import { mergePackageDetailsGalleryImages } from "../../../utils/packageDetailsChatGallery";
import { formatPackageDetailsFlightFacts } from "../../../utils/packageDetailsChatFlights";
import "./PackageDetailsChatView.css";

const heroNavIconSx = {
  fontSize: 22,
  color: "#e0644a",
};

/**
 * Maps server `package_details_view` + fullDeal into a card shape for selectPackageDealForChatContext.
 * @param {Record<string, unknown>|null|undefined} view
 */
export function packageDetailsViewToCard(view) {
  const fd = view?.package_profile_full_deal;
  if (!fd || typeof fd !== "object") return null;
  return {
    offer_rank: view.offer_rank,
    hotel_name: view.hotel_name,
    display_title: view.hotel_name || view.title,
    title: view.title,
    package_profile_full_deal: fd,
    main_image: view.main_image,
    gallery_images: view.gallery_images || [],
    lds_deal_id: view.primary_lds_deal_id,
    price_ils: view.price_ils,
  };
}

function normalizePackageLeg(raw) {
  if (raw == null) return null;
  return Array.isArray(raw) ? raw[0] : raw;
}

/**
 * Drill-down panel for "פרטים על החבילה" (structured API payload).
 */
export default function PackageDetailsChatView({
  view,
  disabled,
  /** בחירת חדר בתוך הצ'אט — פרוטוקול __cvac_select_room__|rank|index */
  onPickRoom,
}) {
  const { airlines } = useAppContext();

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [heroGalleryIndex, setHeroGalleryIndex] = useState(0);
  const [aboutOpen, setAboutOpen] = useState(false);
  const swipeTouchRef = useRef(null);

  const displayGallery = useMemo(() => mergePackageDetailsGalleryImages(view), [view]);

  const displayGalleryKey = displayGallery.join("|");
  useEffect(() => {
    setHeroGalleryIndex(0);
  }, [displayGalleryKey]);

  const heroIndex = Math.min(heroGalleryIndex, Math.max(0, displayGallery.length - 1));
  const heroImageUrl = displayGallery[heroIndex] || "";

  const showGalleryBlock = displayGallery.length > 0;

  const flights = view?.flights || {};
  const about = view?.hotel_about || {};
  const displayRooms = useMemo(() => {
    const r = view?.rooms;
    return Array.isArray(r) ? r : [];
  }, [view?.rooms]);

  const rawFlightLegs = useMemo(() => {
    const fd = view?.package_profile_full_deal;
    const lf = fd?.linkedFlights;
    const { depFlight, retFlight } = getDepRetLegsFromLinkedFlights(lf || {});
    return {
      outbound: normalizePackageLeg(depFlight),
      return: normalizePackageLeg(retFlight),
    };
  }, [view?.package_profile_full_deal]);

  const outboundFlightFacts = useMemo(
    () => formatPackageDetailsFlightFacts(flights.outbound, rawFlightLegs.outbound, airlines),
    [flights.outbound, rawFlightLegs.outbound, airlines]
  );
  const returnFlightFacts = useMemo(
    () => formatPackageDetailsFlightFacts(flights.return, rawFlightLegs.return, airlines),
    [flights.return, rawFlightLegs.return, airlines]
  );
  const hasFlightsBlock = Boolean(outboundFlightFacts || returnFlightFacts);

  if (!view || view.type !== "package_details_view") return null;

  return (
    <section className="hr-cvac-pdv" dir="rtl" aria-label="פרטי חבילה">
      <h4 className="hr-cvac-pdv__heading">{view.title || view.hotel_name || "פרטי חבילה"}</h4>

      <p className="hr-cvac-pdv__price">
        {view.price_ils != null && !Number.isNaN(Number(view.price_ils)) ? (
          <>
            מחיר כולל: ₪{Math.round(Number(view.price_ils)).toLocaleString("he-IL")}
          </>
        ) : (
          "טוען מחיר…"
        )}
      </p>

      {hasFlightsBlock ? (
        <div className="hr-cvac-pdv__block">
          <h5 className="hr-cvac-pdv__sub">טיסות</h5>
          {outboundFlightFacts ? (
            <p className="hr-cvac-pdv__flight-compact">
              <strong className="hr-cvac-pdv__flight-label">הלוך:</strong>{" "}
              <span className="hr-cvac-pdv__flight-facts" dir="ltr" translate="no">
                {outboundFlightFacts}
              </span>
            </p>
          ) : null}
          {returnFlightFacts ? (
            <p className="hr-cvac-pdv__flight-compact">
              <strong className="hr-cvac-pdv__flight-label">חזור:</strong>{" "}
              <span className="hr-cvac-pdv__flight-facts" dir="ltr" translate="no">
                {returnFlightFacts}
              </span>
            </p>
          ) : null}
        </div>
      ) : null}

      {(about.summary || about.full_text || about.location || about.facilities?.length) && (
        <div className="hr-cvac-pdv__block">
          <h5 className="hr-cvac-pdv__sub">אודות המלון</h5>
          {about.location ? <p className="hr-cvac-pdv__loc">מיקום: {about.location}</p> : null}
          {about.stars > 0 ? <p className="hr-cvac-pdv__stars">{about.stars} כוכבים</p> : null}
          {about.summary ? <p className="hr-cvac-pdv__about">{about.summary}</p> : null}
          {about.facilities?.length ? (
            <p className="hr-cvac-pdv__fac">מתקנים: {about.facilities.join(", ")}</p>
          ) : null}
          {about.has_more_description ? (
            <button
              type="button"
              className="hr-cvac-pdv__linkish"
              disabled={disabled}
              onClick={() => setAboutOpen(true)}
            >
              הצג עוד על המלון
            </button>
          ) : null}
        </div>
      )}

      {showGalleryBlock ? (
        <div className="hr-cvac-pdv__block">
          <h5 className="hr-cvac-pdv__sub">גלריה</h5>
          <div className="hr-cvac-pdv__hero">
            <div
              className="hr-cvac-pdv__hero-frame"
              onTouchStart={(e) => {
                if (displayGallery.length < 2 || !e.touches[0]) return;
                const t = e.touches[0];
                swipeTouchRef.current = { x: t.clientX, y: t.clientY };
              }}
              onTouchEnd={(e) => {
                if (displayGallery.length < 2 || disabled) return;
                const start = swipeTouchRef.current;
                swipeTouchRef.current = null;
                const ch = e.changedTouches[0];
                if (!start || !ch) return;
                const dx = ch.clientX - start.x;
                const dy = ch.clientY - start.y;
                if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
                if (dx < 0) {
                  setHeroGalleryIndex((i) => (i + 1) % displayGallery.length);
                } else {
                  setHeroGalleryIndex((i) => (i - 1 + displayGallery.length) % displayGallery.length);
                }
              }}
            >
              {heroImageUrl ? (
                <button
                  type="button"
                  className="hr-cvac-pdv__hero-btn"
                  disabled={disabled}
                  onClick={() => {
                    setLightboxIndex(heroIndex);
                    setLightboxOpen(true);
                  }}
                >
                  <img src={heroImageUrl} alt="" className="hr-cvac-pdv__hero-img" loading="lazy" />
                </button>
              ) : (
                <div className="hr-cvac-pdv__hero-placeholder" aria-hidden />
              )}
              {displayGallery.length > 1 ? (
                <>
                  <button
                    type="button"
                    className="hr-cvac-pdv__hero-nav hr-cvac-pdv__hero-nav--prev"
                    disabled={disabled}
                    aria-label="תמונה קודמת"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setHeroGalleryIndex((i) => (i - 1 + displayGallery.length) % displayGallery.length);
                    }}
                  >
                    <ChevronLeft sx={heroNavIconSx} aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="hr-cvac-pdv__hero-nav hr-cvac-pdv__hero-nav--next"
                    disabled={disabled}
                    aria-label="תמונה הבאה"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setHeroGalleryIndex((i) => (i + 1) % displayGallery.length);
                    }}
                  >
                    <ChevronRight sx={heroNavIconSx} aria-hidden />
                  </button>
                </>
              ) : null}
            </div>
            {displayGallery.length > 1 ? (
              <div className="hr-cvac-pdv__hero-toolbar" dir="rtl">
                <button
                  type="button"
                  className="hr-cvac-pdv__hero-toolbar-btn"
                  disabled={disabled}
                  aria-label="תמונה קודמת"
                  onClick={() =>
                    setHeroGalleryIndex((i) => (i - 1 + displayGallery.length) % displayGallery.length)
                  }
                >
                  <ChevronLeft sx={{ fontSize: 20, color: "#e0644a" }} aria-hidden />
                  קודמת
                </button>
                <span className="hr-cvac-pdv__hero-toolbar-count" translate="no">
                  {heroIndex + 1} / {displayGallery.length}
                </span>
                <button
                  type="button"
                  className="hr-cvac-pdv__hero-toolbar-btn"
                  disabled={disabled}
                  aria-label="תמונה הבאה"
                  onClick={() => setHeroGalleryIndex((i) => (i + 1) % displayGallery.length)}
                >
                  הבא
                  <ChevronRight sx={{ fontSize: 20, color: "#e0644a" }} aria-hidden />
                </button>
              </div>
            ) : null}
          </div>
          {displayGallery.length > 1 ? (
            <div className="hr-cvac-pdv__gallery-meta" aria-live="polite">
              <div className="hr-cvac-pdv__dots" role="tablist" aria-label="בחירת תמונה">
                {displayGallery.map((_, i) => (
                  <button
                    key={`dot-${i}`}
                    type="button"
                    role="tab"
                    aria-selected={i === heroIndex}
                    aria-label={`תמונה ${i + 1} מתוך ${displayGallery.length}`}
                    className={`hr-cvac-pdv__dot${i === heroIndex ? " hr-cvac-pdv__dot--active" : ""}`}
                    disabled={disabled}
                    onClick={() => setHeroGalleryIndex(i)}
                  />
                ))}
              </div>
            </div>
          ) : null}
          <HeroGalleryLightbox
            open={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
            images={displayGallery}
            startIndex={lightboxIndex}
            hotelName={view.hotel_name || ""}
          />
        </div>
      ) : null}

      <div className="hr-cvac-pdv__block">
        <h5 className="hr-cvac-pdv__sub">חדרים זמינים</h5>
        <ChatRoomPicker
          rooms={displayRooms}
          disabled={disabled || !onPickRoom}
          onPickRoom={onPickRoom}
          packagePriceIls={view.price_ils}
        />
      </div>

      {aboutOpen ? (
        <div className="hr-cvac-pdv__sheet-backdrop" role="presentation">
          <button
            type="button"
            className="hr-cvac-pdv__sheet-backdrop-btn"
            aria-label="סגור"
            onClick={() => setAboutOpen(false)}
          />
          <div className="hr-cvac-pdv__sheet" role="dialog" aria-modal="true">
            <div className="hr-cvac-pdv__sheet-head">
              <h5>אודות המלון</h5>
              <button type="button" onClick={() => setAboutOpen(false)} aria-label="סגור">
                ×
              </button>
            </div>
            <div className="hr-cvac-pdv__sheet-body">{about.full_text || about.summary || ""}</div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
